import { Flex, Icon, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { Controller, useFormContext } from 'react-hook-form';

import { calculateMarketAdjustedSalePrice } from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/calculateMarketAdjustedSalePrice.ts';
import { calculateMarketAdjustment } from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/calculateMarketAdjustment.ts';
import {
  DateOfSaleMethodInput,
  calculateDateOfSaleOrListToSaleAdjustment,
} from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import {
  selectAdjustmentSettings,
  selectEffectiveDate,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectMultilineAddress } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import {
  selectConcessionsDisplay,
  selectContractDate,
  selectDateOfSaleDisplay,
  selectListingStatus,
  selectSaleDate,
  selectSalePrice,
  selectSalePriceDisplay,
  selectTermsOfSaleDisplay,
} from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { getColorForStatusOrSubject } from '../../utils/getColorForStatusOrSubject.ts';
import { MarketAdjustmentsModalForm } from './types.ts';
import { formManualAdjustmentsToState } from './utils/formManualAdjustmentsToState.ts';
import { formMarketAdjustmentsToState } from './utils/formMarketAdjustmentsToState.ts';
import { getDateOfSaleOrListToSaleAdjustmentDisplay } from './utils/getDateOfSaleOrListToSaleAdjustmentDisplay.ts';
import { getMarketAdjustedSalePriceDisplay } from './utils/getMarketAdjustedSalePriceDisplay.ts';

const Row = ({ children }: React.PropsWithChildren) => (
  <Flex gap="4px" alignItems="center" paddingX="16px">
    {children}
  </Flex>
);

const Column = ({ children }: React.PropsWithChildren) => <Flex flex={1}>{children}</Flex>;

type Props = {
  compID: string;
  index: number;
  zillowMethodInput: DateOfSaleMethodInput.Zillow | undefined;
  customMonthlyPriceMethodInput: DateOfSaleMethodInput.CustomMonthlyPrice | undefined;
};

export const CompCard = ({
  compID,
  index,
  zillowMethodInput,
  customMonthlyPriceMethodInput,
}: Props) => {
  const { watch, control } = useFormContext<MarketAdjustmentsModalForm>();

  // static display values
  const salePriceDisplay = useAppSelector((state) => selectSalePriceDisplay(state, compID));
  const concessions = useAppSelector((state) => selectConcessionsDisplay(state, compID));
  const termsOfSale = useAppSelector((state) => selectTermsOfSaleDisplay(state, compID));
  const dateOfSaleDisplay = useAppSelector((state) => selectDateOfSaleDisplay(state, compID));

  // data for derived values
  const contractDate = useAppSelector((state) => selectContractDate(state, compID));
  const saleDate = useAppSelector((state) => selectSaleDate(state, compID));
  const price = useAppSelector((state) => selectSalePrice(state, compID));
  const listingStatus = useAppSelector((state) => selectListingStatus(state, compID));
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const adjustmentSettings = useAppSelector(selectAdjustmentSettings);

  const concessionsInput = watch(`manualAdjustments.${compID}.concessions`);
  const termsOfSaleInput = watch(`manualAdjustments.${compID}.termsOfSale`);
  const marketAdjustments = watch('marketAdjustments');

  const mappedManualAdjustments = formManualAdjustmentsToState({
    concessions: concessionsInput,
    termsOfSale: termsOfSaleInput,
  });
  const mappedMarketAdjustments = formMarketAdjustmentsToState(marketAdjustments);

  // derived values
  const dateOfSaleOrListToSaleAdjustment = calculateDateOfSaleOrListToSaleAdjustment({
    price,
    marketAdjustments: mappedMarketAdjustments,
    listingStatus,
    contractDate,
    saleDate,
    effectiveDate,
    zillowMethodInput,
    customMonthlyPriceMethodInput,
  });
  const dateOfSaleOrListToSaleAdjustmentDisplay = getDateOfSaleOrListToSaleAdjustmentDisplay(
    dateOfSaleOrListToSaleAdjustment,
    adjustmentSettings,
  );

  const marketAdjustment = calculateMarketAdjustment({
    manualAdjustments: mappedManualAdjustments,
    dateOfSaleOrListToSaleAdjustment,
  });

  const marketAdjustedSalePrice = calculateMarketAdjustedSalePrice({
    price,
    marketAdjustment,
  });
  const marketAdjustedSalePriceDisplay = getMarketAdjustedSalePriceDisplay(
    marketAdjustedSalePrice,
    adjustmentSettings,
  );

  const addressLines = useAppSelector((state) => selectMultilineAddress(state, compID));
  const pinColor = getColorForStatusOrSubject(listingStatus);

  return (
    <Flex
      minWidth="248px"
      flexDirection="column"
      gap="4px"
      border="1px solid"
      borderColor="strokeDefault"
    >
      <Flex
        flexDirection="column"
        borderBottomColor={pinColor}
        borderBottomWidth="4px"
        borderStyle="solid"
        paddingX="16px"
        paddingY="8px"
      >
        <Flex gap="2px" alignItems="center">
          <Icon icon="map-pin-filled" size="lg" color={pinColor} />
          <Typography variant="tableColumnTitle">Comp {(index + 1).toLocaleString()}</Typography>
        </Flex>
        <Typography variant="subtitle3" truncate color="textTertiary">
          {addressLines[0]}
        </Typography>
        <Typography variant="subtitle3" truncate color="textTertiary">
          {addressLines[1]}
        </Typography>
      </Flex>
      <Flex flexDirection="column" gap="4px" paddingY="8px">
        <Row>
          <Column>
            <Typography>Sale Price</Typography>
          </Column>
          <Column>
            <Typography>{salePriceDisplay}</Typography>
          </Column>
        </Row>
        {termsOfSale !== 'N/A' && (
          <Row>
            <Column>
              <Typography>{termsOfSale}</Typography>
            </Column>
            <Column>
              <Controller
                control={control}
                name={`manualAdjustments.${compID}.termsOfSale`}
                render={({ field }) => <SimpleInput {...field} type="number" size="sm" />}
              />
            </Column>
          </Row>
        )}
        {concessions !== 'N/A' && (
          <Row>
            <Column>
              <Typography>{concessions}</Typography>
            </Column>
            <Column>
              <Controller
                control={control}
                name={`manualAdjustments.${compID}.concessions`}
                render={({ field }) => <SimpleInput {...field} type="number" size="sm" />}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column>
            <Typography>{dateOfSaleDisplay}</Typography>
          </Column>
          <Column>
            <Typography>{dateOfSaleOrListToSaleAdjustmentDisplay}</Typography>
          </Column>
        </Row>
        <Row>
          <Column>
            <Typography variant="h4">Adjusted SP</Typography>
          </Column>
          <Column>
            <Typography variant="h4">{marketAdjustedSalePriceDisplay}</Typography>
          </Column>
        </Row>
      </Flex>
    </Flex>
  );
};
